import { Download, FileText, Loader2, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { FormActions } from "@jbnl/shared-ui";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { datumKort } from "@/features/wedstrijden/datum";
import {
  VERSIESTATUS,
  useGenereerWedstrijdboekje,
  useVerwijderNieuwsteVersie,
  versieHref,
  type Versie,
  type Wedstrijdboekjedetail,
} from "./useWedstrijdboekjes";

/**
 * §3.2.1's versiebeheer, newest first - the reverse of the document's own table: the screen is
 * where you download the current one, the document is where the history reads forwards.
 */
export function Versies({
  seizoenId,
  boekje,
}: {
  seizoenId: string;
  boekje: Wedstrijdboekjedetail;
}) {
  const { t } = useTranslation(["wedstrijdboekjes", "common"]);
  const genereer = useGenereerWedstrijdboekje(seizoenId);
  const terugdraaien = useVerwijderNieuwsteVersie(seizoenId);

  const versies = boekje.boekje.generaties;
  const nieuwste = versies[0];
  const bezig = nieuwste?.status === VERSIESTATUS.bezig;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("versies.titel")}</CardTitle>
        <p className="text-sm text-muted-foreground">{t("versies.uitleg")}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <WijzigingenMelding boekje={boekje} bezig={bezig} />

        <GenereerActie
          boekjeId={boekje.boekje.id}
          bezig={bezig}
          genereer={genereer}
        />

        {versies.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("versies.geen")}</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {versies.map((versie) => (
              <li key={versie.id} className="p-3">
                <Versieregel versie={versie} />
              </li>
            ))}
          </ul>
        )}

        {nieuwste && (
          <TerugdraaienActie
            boekjeId={boekje.boekje.id}
            volgnummer={nieuwste.volgnummer}
            bezig={bezig}
            terugdraaien={terugdraaien}
          />
        )}
      </CardContent>
    </Card>
  );
}

/** Info, not warning: changes since the last version are the ordinary state of a season being
 * planned, and there is nothing to fix. */
function WijzigingenMelding({
  boekje,
  bezig,
}: {
  boekje: Wedstrijdboekjedetail;
  bezig: boolean;
}) {
  const { t } = useTranslation("wedstrijdboekjes");
  const versies = boekje.boekje.generaties;

  if (boekje.heeftWijzigingen && !bezig) {
    return (
      <Alert>
        <span>
          {versies.length === 0
            ? t("versies.eersteVersie")
            : t("versies.wijzigingen")}{" "}
          {boekje.wijziging &&
            t("versies.watErVerandert", { regel: boekje.wijziging })}
        </span>
      </Alert>
    );
  }

  if (boekje.heeftWijzigingen === false && versies.length > 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("versies.geenWijzigingen")}
      </p>
    );
  }

  return null;
}

function GenereerActie({
  boekjeId,
  bezig,
  genereer,
}: {
  boekjeId: string;
  bezig: boolean;
  genereer: ReturnType<typeof useGenereerWedstrijdboekje>;
}) {
  const { t } = useTranslation("wedstrijdboekjes");

  return (
    <FormActions>
      {genereer.error && (
        <p className="text-sm text-destructive">{genereer.error.message}</p>
      )}
      <Button
        onClick={() => genereer.mutate(boekjeId)}
        disabled={bezig || genereer.isPending}
      >
        {bezig ? (
          // sensor:allow codox-spinner pending button, not a loading state
          <Loader2 className="animate-spin" />
        ) : (
          <RefreshCw />
        )}
        {bezig ? t("versies.bezigMaken") : t("versies.maken")}
      </Button>
    </FormActions>
  );
}

function TerugdraaienActie({
  boekjeId,
  volgnummer,
  bezig,
  terugdraaien,
}: {
  boekjeId: string;
  volgnummer: Versie["volgnummer"];
  bezig: boolean;
  terugdraaien: ReturnType<typeof useVerwijderNieuwsteVersie>;
}) {
  const { t } = useTranslation(["wedstrijdboekjes", "common"]);

  return (
    <FormActions>
      {terugdraaien.error && (
        <p className="text-sm text-destructive">{terugdraaien.error.message}</p>
      )}
      <ConfirmDelete
        trigger={
          <Button
            variant="destructive"
            disabled={bezig || terugdraaien.isPending}
          >
            {terugdraaien.isPending
              ? t("common:actions.busy")
              : t("versies.terugdraaien")}
          </Button>
        }
        title={t("versies.terugdraaienVraag", { nummer: volgnummer })}
        description={t("versies.terugdraaienUitleg")}
        confirmLabel={t("versies.terugdraaienConfirm")}
        onConfirm={() => terugdraaien.mutate(boekjeId)}
      />
    </FormActions>
  );
}

function Versieregel({ versie }: { versie: Versie }) {
  const { t } = useTranslation("wedstrijdboekjes");

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
          {t("versies.versie", { nummer: versie.volgnummer })}
          <span className="text-muted-foreground">
            {datumKort(versie.versiedatum)}
          </span>
          <Statusbadge versie={versie} />
          {versie.isConcept && (
            <Badge variant="muted">{t("versies.concept")}</Badge>
          )}
        </p>

        {/* The changelog line the document prints, so the screen and the boekje say the same
            thing about this version - and the failure message where there is one instead. */}
        {versie.wijziging && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {versie.wijziging}
          </p>
        )}
        {versie.foutmelding && (
          <p className="mt-0.5 text-xs text-destructive">
            {versie.foutmelding}
          </p>
        )}
      </div>

      {/* Both artifacts (ADR-0036): JBNL publishes the PDF and patches the .docx where the tool
        cannot say something. */}
      {versie.heeftBestanden && (
        <div className="flex shrink-0 gap-2">
          <Button asChild size="sm" variant="outline">
            <a
              href={versieHref(versie.id, "Pdf")}
              download={versie.bestandsnaam}
            >
              <Download />
              {t("versies.downloadPdf")}
            </a>
          </Button>
          <Button asChild size="sm" variant="outline">
            <a href={versieHref(versie.id, "Docx")}>
              <FileText />
              {t("versies.downloadDocx")}
            </a>
          </Button>
        </div>
      )}
    </div>
  );
}

/** Rendered as a badge but written as the word, so a screen reader says which state this is. */
function Statusbadge({ versie }: { versie: Versie }) {
  const { t } = useTranslation("wedstrijdboekjes");

  if (versie.status === VERSIESTATUS.mislukt) {
    return <Badge variant="destructive">{t("versies.mislukt")}</Badge>;
  }

  if (versie.status === VERSIESTATUS.bezig) {
    return <Badge variant="muted">{t("versies.bezig")}</Badge>;
  }

  return <Badge>{t("versies.geslaagd")}</Badge>;
}
